package pl.nickers.match3

import pl.nickers.match3.ecs.LaserDirection
import pl.nickers.match3.ecs.PowerUpType
import pl.nickers.match3.ecs.TutorialStep

data class GridPos(val row: Int, val col: Int)

data class DebugInfo(
    val phase: String = "IDLE",
    val activatingLaserCount: Int = 0,
    val explodingBombCount: Int = 0,
    val fusedBombCount: Int = 0,
    val fallingCount: Int = 0,
    val swappingCount: Int = 0,
    val frameCount: Int = 0,
    val isAnimatingFlag: Boolean = false,
    val phaseHistory: List<String> = emptyList(),
    val showDebugOverlay: Boolean = false,
)

data class SwapAnimation(
    val from: GridPos,
    val to: GridPos,
    val isReturning: Boolean = false,
)

data class LaserAnimation(
    val origin: GridPos,
    val direction: LaserDirection,
)

data class ScreenTransformInfo(
    val offsetX: Float = 0f,
    val offsetY: Float = 0f,
    val scale: Float = 1f,
    val alpha: Float = 1f,
)

data class JellyCell(
    val type: Int,
    val id: Int,
    val isBomb: Boolean = false,
    val isCrossBomb: Boolean = false,
    val isLaser: Boolean = false,
    val laserDirection: LaserDirection? = null,
    val isColourfulSwirl: Boolean = false,
    val isIceCube: Boolean = false,
    val isEmpty: Boolean = false,
    val isHole: Boolean = false,
    val isSpawnPoint: Boolean = false,
    val spawnPointVisible: Boolean = true,
    val iceCubeLife: Int = 3,
    val bodyImage: String = "jelly_1.png",
    val faceImage: String = "face_1.png",
    val isDraggable: Boolean = true,
    val isFused: Boolean = false,
    /** E5: the piece in this cell is cursed — rendered with a shadow veil. */
    val isCursed: Boolean = false,
    /** E5: this cell's floor is a Void Puddle terrain tile (curses passing pieces). */
    val isVoidPuddle: Boolean = false,
    /** E5/E6: this cell's floor is a Purified Rune terrain tile (cleanses on match). */
    val isPurifiedRune: Boolean = false,
    val screenTransform: ScreenTransformInfo = ScreenTransformInfo(),
)

data class GoalInfo(
    val pieceId: String,
    val bodyImage: String,
    val remainingQuantity: Int,
    val targetQuantity: Int,
    val completed: Boolean,
)

data class MoveLimitInfo(
    val remaining: Int,
    val total: Int,
)

data class PowerUpInfo(
    val type: PowerUpType,
    val uses: Int,
    val entityId: Int,
)

data class FamiliarInfo(
    val progress: Int,
    val threshold: Int,
)

data class BombPowerUpFlightUi(
    val entityId: Int,
    val targetRow: Int,
    val targetCol: Int,
    val powerUpEntityId: Int,
)

data class GoalJellyFlightUi(
    val entityId: Int,
    val row: Int,
    val col: Int,
    val bodyImage: String,
    val faceImage: String,
    val goalPieceId: String,
)

/** A sprite flying from the familiar display to the ([row], [col]) cell it transmuted. */
data class FamiliarFlightUi(
    val entityId: Int,
    val row: Int,
    val col: Int,
    val bodyImage: String,
    val faceImage: String,
)

data class FallAnimationInfo(
    val fromRow: Int,
    val toRow: Int,
    val fromCol: Int,
    val toCol: Int,
)

typealias FallingCells = Map<Int, FallAnimationInfo>

data class TeleportAnimationInfo(
    val sourceRow: Int,
    val sourceCol: Int,
    val destRow: Int,
    val destCol: Int,
)

typealias TeleportingCells = Map<Int, TeleportAnimationInfo>

/**
 * Static link displayed on the board: a one-way teleport from [source] to [destination].
 */
data class TeleportLinkUi(
    val source: GridPos,
    val destination: GridPos,
)

data class SwirlVacuumInfo(
    val fromRow: Int,
    val fromCol: Int,
    val targetRow: Int,
    val targetCol: Int,
)

/**
 * High-level mode that controls how cell taps and drags are interpreted.
 *
 * [SWAP] — default. Taps select / second-tap to swap. Drags swap adjacent cells.
 * [ROW_BOMB_TARGETING] — row-bomb power-up is armed. The next tap fires the wave;
 * drags are ignored so the user can't accidentally consume their power-up by swiping.
 *
 * Future modes (e.g. column-bomb, swap-prediction overlay) can be added without
 * touching the gesture-handling layer beyond a new `when` branch.
 */
enum class InputMode { SWAP, ROW_BOMB_TARGETING, TARGETED_REMOVE_TARGETING, TELEPORT_LINKING }

/**
 * Render-facing snapshot of the active tutorial step, built by [pl.nickers.match3.ecs.RenderSystem]
 * from the ECS-owned `TutorialComponent`. [highlightFrom]/[highlightTo] are only
 * populated for [TutorialStep.SWAP_HINT].
 */
data class TutorialOverlayUi(
    val step: TutorialStep,
    val message: String,
    val highlightFrom: GridPos? = null,
    val highlightTo: GridPos? = null,
)

data class GameState(
    val grid: List<List<JellyCell>>,
    val width: Int = GRID_SIZE,
    val height: Int = GRID_SIZE,
    val selected: GridPos? = null,
    val swappingCells: Map<Int, SwapAnimation> = emptyMap(),
    val score: Int = 0,
    val disableNewJellySpawn: Boolean = false,
    val fallingCells: FallingCells = emptyMap(),
    val activatingLasers: List<LaserAnimation> = emptyList(),
    val explodingBombs: List<GridPos> = emptyList(),
    val fullBoardExplosion: Boolean = false,
    val fusedBombs: List<GridPos> = emptyList(),
    val debugInfo: DebugInfo = DebugInfo(),
    val selectedEntityComponents: List<String> = emptyList(),
    val goals: List<GoalInfo> = emptyList(),
    val moveLimit: MoveLimitInfo? = null,
    val won: Boolean = false,
    val lost: Boolean = false,
    /** Stars (0–3) earned on the most recent journey win; 0 until won or outside journey mode. */
    val starsEarned: Int = 0,
    /** True when [starsEarned] raised the stored best for this level (a new personal best). */
    val isNewStarBest: Boolean = false,
    val backgroundImageBase64: String? = null,
    val powerUps: List<PowerUpInfo> = emptyList(),
    val isJourneyMode: Boolean = false,
    val isLastJourneyLevel: Boolean = false,
    val isTestLevel: Boolean = false,
    val bombPowerUpFlight: BombPowerUpFlightUi? = null,
    val goalJellyFlights: List<GoalJellyFlightUi> = emptyList(),
    val familiarFlights: List<FamiliarFlightUi> = emptyList(),
    val inputMode: InputMode = InputMode.SWAP,
    val swirlVacuumCells: Map<Int, SwirlVacuumInfo> = emptyMap(),
    val teleportingCells: TeleportingCells = emptyMap(),
    val teleportLinks: List<TeleportLinkUi> = emptyList(),
    val teleportLinkingDestination: GridPos? = null,
    val familiar: FamiliarInfo? = null,
    val tutorial: TutorialOverlayUi? = null,
)
